import { useState } from 'react'
import type { AgentTurnDisclosure } from '@/gen/cortado/v1/agent_pb'
import { AnimatedCollapse, CollapseChevron } from '@/components/shared/animated-collapse'
import { formatToolName } from './chat-utils'
import { cn } from '@/lib/utils'

interface UsedForThisAnswerProps {
  turnId?: string
  disclosure?: AgentTurnDisclosure
  isLoading?: boolean
  onExpand?: () => void
}

function sourceCount(disclosure: AgentTurnDisclosure): number {
  return disclosure.packs.length + disclosure.toolCalls.length
}

function isEmptyDisclosure(disclosure: AgentTurnDisclosure): boolean {
  return disclosure.packs.length === 0
    && disclosure.toolCalls.length === 0
    && !disclosure.model
    && disclosure.decisions.length === 0
    && disclosure.warnings.length === 0
}

function formatCacheTokens(value?: bigint): string {
  if (value === undefined) return 'not reported'
  return value.toString()
}

export function UsedForThisAnswer({
  turnId,
  disclosure,
  isLoading = false,
  onExpand,
}: UsedForThisAnswerProps) {
  const [open, setOpen] = useState(false)
  const loaded = disclosure !== undefined
  const emptyAfterLoad = loaded && isEmptyDisclosure(disclosure)
  const count = loaded ? sourceCount(disclosure) : 0

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) onExpand?.()
  }

  let collapsedLabel: string
  if (!turnId || emptyAfterLoad) {
    collapsedLabel = 'No disclosure recorded for this turn'
  } else if (isLoading && !loaded) {
    collapsedLabel = 'Loading disclosure…'
  } else if (!loaded) {
    collapsedLabel = 'Used for this answer'
  } else {
    collapsedLabel = `Used for this answer · ${count} source${count === 1 ? '' : 's'}`
  }

  return (
    <div data-testid="used-for-this-answer">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => handleOpenChange(!open)}
        className={cn(
          'flex w-full items-center gap-1.5 py-1 text-left text-xs text-muted-foreground',
          'hover:text-muted-foreground/90',
        )}
        disabled={!turnId}
      >
        <CollapseChevron open={open} className="size-3.5 shrink-0" />
        <span>{collapsedLabel}</span>
      </button>

      <AnimatedCollapse open={Boolean(turnId) && open}>
        <div className="space-y-3 pt-2 text-xs text-muted-foreground">
          {loaded && !emptyAfterLoad && (
            <>
              {disclosure!.packs.length > 0 && (
                <section aria-label="Packs">
                  <h4 className="mb-1 font-medium text-foreground/80">Packs</h4>
                  <ul className="space-y-1">
                    {disclosure!.packs.map((pack) => (
                      <li key={`${pack.packId}-${pack.version}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="font-mono text-xs">{pack.packId}</span>
                        <span>{pack.version}</span>
                        {pack.loadedDuringTurn && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">loaded during this answer</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {disclosure!.toolCalls.length > 0 && (
                <section aria-label="Tools">
                  <h4 className="mb-1 font-medium text-foreground/80">Tools</h4>
                  <ul className="space-y-1">
                    {disclosure!.toolCalls.map((call) => (
                      <li key={call.id || call.toolCallId} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>{formatToolName(call.toolName)}</span>
                        <span className="font-mono text-xs">{call.packId}</span>
                        <span>{call.permission}</span>
                        {call.durationMs !== undefined && (
                          <span>{call.durationMs.toString()} ms</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {disclosure!.model && (
                <section aria-label="Model">
                  <h4 className="mb-1 font-medium text-foreground/80">Model</h4>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                    <dt>Provider</dt>
                    <dd>{disclosure!.model.provider}</dd>
                    <dt>Requested</dt>
                    <dd className="font-mono text-xs">{disclosure!.model.requestedModel}</dd>
                    <dt>Resolved</dt>
                    <dd className="font-mono text-xs">{disclosure!.model.resolvedModel}</dd>
                    <dt>Cache tokens</dt>
                    <dd>{formatCacheTokens(disclosure!.model.cacheReadInputTokens)}</dd>
                  </dl>
                </section>
              )}

              {disclosure!.decisions.length > 0 && (
                <section aria-label="Policy decisions">
                  <h4 className="mb-1 font-medium text-foreground/80">Policy decisions</h4>
                  <ul className="space-y-1">
                    {disclosure!.decisions.map((decision, index) => (
                      <li key={`${decision.actionType}-${index}`}>
                        <span className="font-medium">{decision.actionType}</span>
                        {' · '}
                        <span>{decision.outcome}</span>
                        {decision.reason && (
                          <>
                            {' — '}
                            <span>{decision.reason}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {disclosure!.warnings.length > 0 && (
                <section aria-label="Warnings">
                  <h4 className="mb-1 font-medium text-foreground/80">Warnings</h4>
                  <ul className="list-disc pl-4">
                    {disclosure!.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}

          {emptyAfterLoad && (
            <p>No disclosure recorded for this turn</p>
          )}
        </div>
      </AnimatedCollapse>
    </div>
  )
}
